import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { DEV_MODE_USERNAME } from "../config";
import { ApiError, createApiClient } from "./client";
import type { ApiClient, AuthEvent } from "./client";
import { queryKeys } from "./keys";
import type { AuthConfig } from "./types";

interface ApiContextValue {
  client: ApiClient;
  /**
   * True once the server answered `401` to anything.
   *
   * There is no session at all any more, so every screen must give way to the
   * sign-in screen — a dataset page polling in the background would otherwise
   * keep asking questions nobody is allowed to ask.
   */
  sessionLost: boolean;
}

const ApiContext = createContext<ApiContextValue | null>(null);

/**
 * Build the react-query cache used by every screen.
 *
 * Returns:
 *     A client that never retries an answered request — a 404 means "there is
 *     no such dataset", a 503 means "the console has not been told where files
 *     are kept", and both are states to show, not failures to hide behind a
 *     spinner. Only requests that never reached the server are retried.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount: number, error: Error): boolean => {
          if (error instanceof ApiError) {
            return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
        staleTime: 1_000,
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Identity headers to add to a request, given what the server published.
 *
 * With Google sign-in this is empty: the identity is an HttpOnly cookie the
 * browser attaches by itself and this page cannot read. The server's
 * development mode is the one exception — it authenticates nobody and trusts a
 * name in a header — and the interface says so on every screen.
 *
 * Args:
 *     config: The published authentication configuration, if it arrived.
 *
 * Returns:
 *     The headers to merge into the request.
 */
function identityHeadersFor(
  config: AuthConfig | undefined,
): Record<string, string> {
  return config?.mode === "dev" ? { "X-User": DEV_MODE_USERNAME } : {};
}

interface ApiProviderProps {
  children: ReactNode;
  /** Injected in tests; production builds talk to the real server. */
  fetchImpl?: typeof fetch;
}

/**
 * Provide the typed API client and the query cache.
 *
 * The client reports the two answers that are about *who* is calling rather
 * than about the call: a `401` empties the cache and raises `sessionLost`, and
 * a `403` on a pending or disabled account re-reads `/me` so the account
 * screen appears wherever the person happened to be.
 *
 * Args:
 *     children: Application subtree.
 *     fetchImpl: Optional `fetch` override (tests).
 *
 * Returns:
 *     A provider wrapping the subtree.
 */
export function ApiProvider({
  children,
  fetchImpl,
}: ApiProviderProps): JSX.Element {
  const [queryClient] = useState(createQueryClient);
  const [sessionLost, setSessionLost] = useState(false);

  const onAuthEvent = useCallback(
    (event: AuthEvent): void => {
      if (event === "unauthenticated") {
        setSessionLost(true);
        // Nothing is kept across identities: whoever signs in next starts from
        // an empty cache, never from the previous person's screens — and this
        // console's screens hold a client's file names. The one exception
        // carries no identity at all: how this deployment signs people in is
        // public, served without a session, and is precisely what the sign-in
        // screen about to be rendered needs.
        queryClient.removeQueries({
          predicate: (query) => query.queryKey[0] !== queryKeys.authConfig[0],
        });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
    [queryClient],
  );

  const client = useMemo<ApiClient>(
    () =>
      createApiClient({
        fetchImpl,
        onAuthEvent,
        identityHeaders: () =>
          identityHeadersFor(
            queryClient.getQueryData<AuthConfig>(queryKeys.authConfig),
          ),
      }),
    [fetchImpl, onAuthEvent, queryClient],
  );

  const value = useMemo<ApiContextValue>(
    () => ({ client, sessionLost }),
    [client, sessionLost],
  );

  return (
    <ApiContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ApiContext.Provider>
  );
}

function useApiContext(): ApiContextValue {
  const value = useContext(ApiContext);
  if (!value) {
    throw new Error("useApi must be used inside an ApiProvider");
  }
  return value;
}

/**
 * Access the typed API client.
 *
 * Returns:
 *     The API client of the running application.
 *
 * Raises:
 *     Error: When called outside of an `ApiProvider`.
 */
export function useApi(): ApiClient {
  return useApiContext().client;
}

/**
 * Whether the server has refused this browser's session.
 *
 * Returns:
 *     True once anything answered `401`.
 */
export function useSessionLost(): boolean {
  return useApiContext().sessionLost;
}
